import React, { useCallback, useEffect, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
export function TestimonialsSection() {
  const testimonials = [{
    name: 'Dominique',
    role: 'DevRel Lead, Phox',
    content: 'Turnout finally gave me a way to actually get developers to show up. I used to spend hours manually messaging registrants before every meetup. Now Turnout handles the personalized outreach, our no-show rates have plummeted, and I can focus on the content.',
    image: 'https://cdn.magicpatterns.com/uploads/9WWS2deHaJvdr1GaZfEQ1x/0010_Brio_Photography.png',
    logo: 'https://cdn.magicpatterns.com/uploads/s8Y6nzsiuRyEvP2FHhV2kh/33c952_4fddab6300d04fb0954d3cb229081395~mv2.png',
    link: 'https://www.phoxevents.com/about',
    stars: 5
  }, {
    name: 'Busola',
    role: 'Community Manager, Buflus',
    content: 'Managing RSVPs across multiple community events used to mean juggling a dozen spreadsheets and sending endless manual DMs. Turnout brought all of that into one system. I never have to manually message a registrant again.',
    image: 'https://cdn.magicpatterns.com/uploads/9RHmZAnzM5Y1qc8grqc9FK/download.jpg',
    imageStyle: {
      objectFit: 'cover',
      objectPosition: '50% 18%',
      transform: 'scale(1.6)',
      transformOrigin: '50% 18%'
    },
    logo: 'https://cdn.magicpatterns.com/uploads/fJSy8iCi2m32tkwWiRKNNp/cropped-BUFLUS_logo-removebg-preview_(1).png',
    link: 'https://buflusevents.com/',
    stars: 5
  }, {
    name: 'Founder',
    role: 'On Dot Meetups',
    content: 'We run highly curated events where every attendee matters. Turnout handles our entire outreach process automatically. It is incredible to see attendance rates climb without spending hours on manual follow-ups.',
    image: 'https://cdn.magicpatterns.com/uploads/4EWdPChyEYXuKDWKCzpKBL/logo.png',
    imageClass: 'object-contain bg-gray-900 p-3',
    link: 'https://ondotexperience.com/',
    stars: 5
  }];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const nextSlide = useCallback(() => {
    setCurrentIndex(prevIndex => prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1);
  }, [testimonials.length]);
  const prevSlide = useCallback(() => {
    setCurrentIndex(prevIndex => prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1);
  }, [testimonials.length]);
  const goToSlide = index => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        nextSlide();
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAutoPlaying, nextSlide]);
  const handleControlClick = callback => {
    setIsAutoPlaying(false);
    callback();
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };
  return <section id="testimonials" className="py-20 bg-white" data-id="element-320">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-321">
        <AnimatedSection data-id="element-322">
          <div className="text-center mb-16" data-id="element-323">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-324">
              Why They Didn't Hire
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto" data-id="element-325">
              Community leaders who chose Turnout over hiring event
              coordinators.
            </p>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.2} data-id="element-326">
          <div className="relative max-w-5xl mx-auto" data-id="element-327">
            <div className="overflow-hidden" data-id="element-328">
              <div className="flex transition-transform duration-500 ease-in-out" style={{
              transform: `translateX(-${currentIndex * 100}%)`
            }} data-id="element-329">
                {testimonials.map((testimonial, index) => <div key={index} className="w-full flex-shrink-0 px-4" data-id="element-330">
                    <div className="bg-gray-50 rounded-2xl p-10 md:p-12 flex flex-col md:flex-row items-center md:items-start gap-8 border border-gray-100" data-id="element-331">
                      <div className="flex-shrink-0 flex flex-col items-center gap-3" data-id="element-332">
                        <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-white shadow-md" data-id="element-333">
                          {testimonial.image ? <img src={testimonial.image} alt={testimonial.name} className={`h-full w-full ${testimonial.imageClass || 'object-cover'}`} style={testimonial.imageStyle || {}} data-id="element-334" /> : <div className={`h-full w-full ${testimonial.initialsColor || 'bg-indigo-600'} flex items-center justify-center`} data-id="element-335">
                              <span className="text-white text-3xl font-bold" data-id="element-336">
                                {testimonial.initials}
                              </span>
                            </div>}
                        </div>
                        {testimonial.logo && <a href={testimonial.link} target="_blank" rel="noopener noreferrer" className="block" data-id="element-337">
                            <img src={testimonial.logo} alt="Company logo" className="h-8 object-contain opacity-80 hover:opacity-100 transition-opacity" data-id="element-338" />
                          </a>}
                      </div>
                      <div className="flex-grow text-center md:text-left" data-id="element-339">
                        <Quote className="h-8 w-8 text-indigo-200 mb-4 mx-auto md:mx-0" data-id="element-340" />
                        <p className="text-gray-800 text-xl md:text-2xl font-medium italic mb-6 leading-relaxed" data-id="element-341">
                          "{testimonial.content}"
                        </p>
                        <div data-id="element-342">
                          {testimonial.link ? <a href={testimonial.link} target="_blank" rel="noopener noreferrer" className="group" data-id="element-343">
                              <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors" data-id="element-344">
                                {testimonial.name}
                              </h3>
                              <p className="text-indigo-600 font-medium group-hover:underline" data-id="element-345">
                                {testimonial.role}
                              </p>
                            </a> : <>
                              <h3 className="text-lg font-bold text-gray-900" data-id="element-346">
                                {testimonial.name}
                              </h3>
                              <p className="text-indigo-600 font-medium" data-id="element-347">
                                {testimonial.role}
                              </p>
                            </>}
                        </div>
                        <div className="flex justify-center md:justify-start mt-4" data-id="element-348">
                          {[...Array(testimonial.stars)].map((_, i) => <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" data-id="element-349" />)}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            <button onClick={() => handleControlClick(prevSlide)} className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 md:-ml-12 bg-white rounded-full p-3 shadow-lg text-gray-600 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 z-10 transition-colors" aria-label="Previous testimonial" data-id="element-350">
              <ChevronLeft size={24} data-id="element-351" />
            </button>
            <button onClick={() => handleControlClick(nextSlide)} className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 md:-mr-12 bg-white rounded-full p-3 shadow-lg text-gray-600 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 z-10 transition-colors" aria-label="Next testimonial" data-id="element-352">
              <ChevronRight size={24} data-id="element-353" />
            </button>

            <div className="flex justify-center space-x-3 mt-8" data-id="element-354">
              {testimonials.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`w-3 h-3 rounded-full focus:outline-none transition-colors ${index === currentIndex ? 'bg-indigo-600' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-355" />)}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}